import { useState } from "react";
import { Link } from "react-router-dom";
import { Flame, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { categoryLabel } from "../lib/categories";
import Reveal from "./Reveal";

export default function OpinionCard({ opinion, index = 0, onOpen }) {
  const { user, openAuth } = useAuth();
  const [upvotes, setUpvotes] = useState(opinion.upvotes || 0);
  const [upvoted, setUpvoted] = useState(!!opinion.upvoted);

  const handleUpvote = async (e) => {
    e.stopPropagation();
    if (!user) {
      openAuth("login");
      return;
    }
    try {
      const { data } = await api.post(`/opinions/${opinion.id}/upvote`);
      setUpvotes(data.upvotes);
      setUpvoted(data.upvoted);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Reveal delay={(index % 6) * 0.06}>
      <div data-testid="community-opinion-card" onClick={() => onOpen && onOpen(opinion)}
        className="group cursor-pointer border border-white/10 bg-surface hover:border-kinetic/40 p-6 transition-colors duration-300 h-full flex flex-col">
        {opinion.image_url && (
          <div className="-mx-6 -mt-6 mb-5 overflow-hidden aspect-[16/8]">
            <img src={opinion.image_url} alt={opinion.title} loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
          </div>
        )}
        <div className="flex items-center justify-between mb-4">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-kinetic">{categoryLabel(opinion.category)}</span>
          {opinion.author_id && opinion.author_id !== "seed" ? (
            <Link to={`/member/${opinion.author_id}`} onClick={(e) => e.stopPropagation()} data-testid="opinion-author-link"
              className="text-[10px] font-mono text-ash hover:text-kinetic transition-colors">@{opinion.author_name}</Link>
          ) : (
            <span className="text-[10px] font-mono text-ash">@{opinion.author_name}</span>
          )}
        </div>
        <h3 className="font-display font-bold text-lg leading-snug group-hover:text-kinetic transition-colors duration-300">
          {opinion.title}
        </h3>
        <p className="text-ash text-sm leading-relaxed mt-3 line-clamp-3 flex-1">{opinion.body}</p>
        <div className="flex items-center gap-4 mt-5 pt-4 border-t border-white/5">
          <button data-testid="opinion-upvote-button" onClick={handleUpvote}
            className={`flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full border transition-colors duration-300 ${upvoted ? "border-crimson/60 text-crimson bg-crimson/10" : "border-white/10 text-ash hover:text-crimson hover:border-crimson/40"}`}>
            <Flame size={13} fill={upvoted ? "currentColor" : "none"} /> {upvotes}
          </button>
          <span className="flex items-center gap-1.5 text-xs font-mono text-ash">
            <MessageSquare size={13} /> {opinion.comment_count || 0}
          </span>
        </div>
      </div>
    </Reveal>
  );
}
